import type {
  Order,
  OrdersPayload,
  OrdersResponse,
  OrdersItemStatusChangePayload,
  OrdersItemChangePayload,
  OrderItemElementPayload,
  AddOrderItemElementPayload,
  EditOrderItemElementPayload,
  IOrderCompound,
  OrdersItemCreatePayload,
} from '@/types/orders'
import type { ItemPayload, DeleteSuccessResponse } from '@/types/main'
import axios from 'axios'

const getOrdersCall = async (
  abortController?: AbortController,
  params?: OrdersPayload,
): Promise<OrdersResponse> => {
  const { data } = await axios.get('/dashboard/v1/chief/market/orders', {
    signal: abortController?.signal,
    params,
  })
  return data.data
}

const getOrdersItemCall = async (
  abortController?: AbortController,
  params?: ItemPayload,
): Promise<Order> => {
  const { data } = await axios.get(`/dashboard/v1/chief/market/orders/${params?.id}`, {
    signal: abortController?.signal,
  })
  return data.data
}

const updateOrdersItemStatusCall = async (
  abortController?: AbortController,
  params?: OrdersItemStatusChangePayload,
): Promise<Order> => {
  const { data } = await axios.patch(
    `/dashboard/v1/chief/market/orders/${params?.id}`,
    { ...params?.data },
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

const createOrdersItemCall = async (
  abortController?: AbortController,
  params?: OrdersItemCreatePayload,
): Promise<Order> => {
  const { data } = await axios.post(
    '/dashboard/v1/chief/market/orders',
    { ...params },
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

const updateOrdersItemCall = async (
  abortController?: AbortController,
  params?: OrdersItemChangePayload,
): Promise<Order> => {
  const { data } = await axios.put(
    `/dashboard/v1/chief/market/orders/${params?.id}`,
    { ...params?.data },
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

const addOrdersItemElementCall = async (
  abortController?: AbortController,
  params?: AddOrderItemElementPayload,
): Promise<IOrderCompound> => {
  const { data } = await axios.post(
    `/dashboard/v1/chief/market/orders/${params?.id}/item`,
    { ...params?.data },
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

const editOrdersItemElementCall = async (
  abortController?: AbortController,
  params?: EditOrderItemElementPayload,
): Promise<IOrderCompound> => {
  const { data } = await axios.patch(
    `/dashboard/v1/chief/market/orders/${params?.orderId}/item/${params?.elemId}`,
    { ...params?.data },
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

const deleteOrdersItemElementCall = async (
  abortController?: AbortController,
  params?: OrderItemElementPayload,
): Promise<DeleteSuccessResponse> => {
  const { data } = await axios.delete(
    `/dashboard/v1/chief/market/orders/${params?.orderId}/item/${params?.elemId}`,
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

const sendOrderPaymentLinkCall = async (
  abortController?: AbortController,
  params?: ItemPayload,
): Promise<DeleteSuccessResponse> => {
  const { data } = await axios.post(
    `/dashboard/v1/chief/market/orders/${params?.id}/payment-link`,
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

export {
  addOrdersItemElementCall,
  createOrdersItemCall,
  deleteOrdersItemElementCall,
  editOrdersItemElementCall,
  getOrdersCall,
  getOrdersItemCall,
  sendOrderPaymentLinkCall,
  updateOrdersItemCall,
  updateOrdersItemStatusCall,
}

// Отправление заказа во внешней службе доставки. Ответ — как отдаёт сервер, без camelCase.
export interface OrderShipment {
  id: number
  service: string
  number: string | null
  status: string
  status_title: string
  status_text: string | null
  tracking_url: string | null
  courier: { name?: string, car?: string, phone?: string } | null
  price: number | null
  error: string | null
  // Связь со службой оборвалась при создании: после сверки заказ отправляется снова кнопкой.
  can_retry?: boolean
  updated_at: string | null
}

const shipmentPath = (orderId: number | string) => `/dashboard/v1/chief/market/orders/${orderId}/shipment`

export const getOrderShipmentCall = async (orderId: number | string): Promise<{ provider: string | null, provider_title?: string | null, shipment: OrderShipment | null }> =>
  (await axios.get(shipmentPath(orderId))).data

export const createOrderShipmentCall = async (orderId: number | string): Promise<OrderShipment> =>
  (await axios.post(shipmentPath(orderId))).data.shipment

export const refreshOrderShipmentCall = async (orderId: number | string): Promise<OrderShipment> =>
  (await axios.post(`${shipmentPath(orderId)}/refresh`)).data.shipment

export const cancelOrderShipmentCall = async (orderId: number | string): Promise<OrderShipment> =>
  (await axios.delete(shipmentPath(orderId))).data.shipment
